<template>
  <div data-locator="features">
    <div class="text-body-1 font-weight-bold pb-5 ml-1">
      {{ $t('project.project-settings.ProjectSettingsFeatures.title') }}
    </div>

    <!-- QA Authors -->
    <AppCard
      v-if="isCurrentUserPm"
      id="hideQaAuthorsCard"
      class="mb-4"
      data-locator="hide-qa-authors-settings"
    >
      <div class="d-flex flex-column flex-sm-row align-start align-sm-center">
        <div class="d-flex flex-column flex-grow-1 pr-0 pr-sm-6">
          <p class="text--medium text--bold">
            {{ $t('project.project-settings.ProjectSettingsFeatures.hideQaAuthors.title') }}
          </p>
          <p class="mb-0 text--small">
            {{ $t('project.project-settings.ProjectSettingsFeatures.hideQaAuthors.description') }}
          </p>
        </div>

        <div class="d-flex justify-end mt-1 mt-sm-0">
          <app-toggle
            :value="localHideQaAuthors"
            :loading="toggleHideAuthorsLoading"
            color="darkblue-500"
            iconColor="darkblue-500"
            iconSize="xxxs"
            hide-details
            data-locator="hide-qa-authors-toggle"
            @input="onToggleHideQaAuthors"
          />
        </div>
      </div>
    </AppCard>

    <!-- NDA -->
    <AppCard data-locator="nda-settings">
      <div class="d-flex flex-column flex-sm-row align-start align-sm-center">
        <div class="d-flex flex-column flex-grow-1 pr-0 pr-sm-6">
          <div class="d-flex align-center">
            <p class="text--medium text--bold">
              {{ $t('project.project-settings.ProjectSettingsFeatures.nda.title') }}
            </p>
            <template v-if="hasNda">
              <app-icon-badge
                color="success"
                class="mb-2 ml-3 mr-2"
                :bordered="false"
                dot
              />
              <p class="text-success text--small text--bold">
                {{ $t("project.project-settings.ProjectSettingsFeatures.nda.activated") }}
              </p>
            </template>
          </div>

          <p class="mb-0 text--small">
            {{ $t('project.project-settings.ProjectSettingsFeatures.nda.ndaLabel') }}
          </p>
        </div>

        <div class="d-flex justify-end mt-1 mt-sm-0">
          <app-button
            v-if="!hasNda"
            data-locator="activate-button"
            type="outlined"
            variant="neutral"
            :disabled="toggleNdaLoading"
            :loading="toggleNdaLoading"
            @click="isNdaActivationDialogOpen = true"
          >
            {{ $t('common.activate') }}...
          </app-button>
          <app-button
            v-else
            data-locator="delete-button"
            type="outlined"
            variant="neutral"
            :disabled="toggleNdaLoading"
            :loading="toggleNdaLoading"
            @click="isNdaDeletionDialogOpen = true"
          >
            {{ $t("common.delete") }}
          </app-button>
        </div>
      </div>
    </AppCard>

    <NdaDialog
      v-if="isNdaActivationDialogOpen"
      :mnemo="mnemo"
      @close="isNdaActivationDialogOpen = false"
      @nda-added="isNdaAddedSignDialogOpen = true"
    />
    <NdaDeletionDialog
      v-if="isNdaDeletionDialogOpen"
      @close="isNdaDeletionDialogOpen = false"
      @confirm="prepareToggleNdaDataroomAccess"
    />
    <NdaAddedSignDialog
      v-if="isNdaAddedSignDialogOpen"
      :mnemo="mnemo"
      @close="isNdaAddedSignDialogOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ComputedRef, ref, watch } from 'vue'
import { useStore } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import { toast } from '@/common/utils/toast'
import i18n from '@/plugins/i18n'
import NdaAddedSignDialog from '@/project/nda/dialogs/NdaAddedSignDialog.vue'
import NdaDeletionDialog from '@/project/nda/dialogs/NdaDeletionDialog.vue'
import NdaDialog from '@/project/nda/dialogs/NdaDialog.vue'
import { PATCH_ROOM } from '@/store/modules/room/action_types'
import { SET_CURRENT_ROOM } from '@/store/modules/room/mutation_types'

export default defineComponent({
  name: 'ProjectSettingsDataroomFeatures',
  components: {
    NdaDialog,
    NdaDeletionDialog,
    NdaAddedSignDialog,
    AppCard,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const store = useStore()
    const currentRoom = computed(() => store.state.room.currentRoom)
    const isCurrentUserPm = computed(() => store.getters['room/isCurrentUserPm'])

    const toggleHideAuthorsLoading = ref(false)
    const localHideQaAuthors = ref<boolean>(false)

    watch(
      () => currentRoom.value?.hideQaAuthors,
      (v) => {
        localHideQaAuthors.value = Boolean(v)
      },
      { immediate: true },
    )

    const onToggleHideQaAuthors = async (value: boolean): Promise<void> => {
      if (toggleHideAuthorsLoading.value) return
      if (value === Boolean(currentRoom.value?.hideQaAuthors)) return
  
      try {
        toggleHideAuthorsLoading.value = true

        await store.dispatch(`room/${PATCH_ROOM}`, {
          mnemo: props.mnemo,
          data: {
            hideQaAuthors: value,
          },
        })

        if (currentRoom.value) {
          currentRoom.value.hideQaAuthors = value
          store.commit(`room/${SET_CURRENT_ROOM}`, currentRoom.value)
        }

        toast.success(
          value
            ? i18n.global.t('project.project-settings.ProjectSettingsFeatures.hideQaAuthors.successOn')
            : i18n.global.t('project.project-settings.ProjectSettingsFeatures.hideQaAuthors.successOff'),
        )
      } catch (e) {
        console.error(e)
        toast.error(i18n.global.t('project.project-settings.ProjectSettingsFeatures.hideQaAuthors.error'))
        localHideQaAuthors.value = Boolean(currentRoom.value?.hideQaAuthors)
      } finally {
        toggleHideAuthorsLoading.value = false
      }
    }

    const hasNda: ComputedRef<boolean | undefined> = computed(() => currentRoom.value?.hasNda)
    const isNdaActivationDialogOpen = ref(false)
    const isNdaDeletionDialogOpen = ref(false)
    const isNdaAddedSignDialogOpen = ref(false)
    const toggleNdaLoading = ref(false)

    const prepareToggleNdaDataroomAccess = async (): Promise<void> => {
      try {
        toggleNdaLoading.value = true
        const newValue = !hasNda.value
        await store.dispatch(`room/${PATCH_ROOM}`, {
          mnemo: props.mnemo,
          data: {
            optNda: newValue,
          },
        })
        if (currentRoom.value) {
          currentRoom.value.hasNda = !hasNda.value
          store.commit(`room/${SET_CURRENT_ROOM}`, currentRoom.value)
        }
        if (newValue) {
          toast.success(i18n.global.t('project.project-settings.ProjectSettingsFeatures.nda.ndaActivationSuccess'))
        } else {
          toast.success(i18n.global.t('project.project-settings.ProjectSettingsFeatures.nda.ndaDeactivationSuccess'))
        }
      } catch (error) {
        console.error(error)
        toast.error(i18n.global.t('project.project-settings.ProjectSettingsFeatures.nda.sndaActivationError'))
      } finally {
        toggleNdaLoading.value = false
      }
    }

    return {
      isCurrentUserPm,
      localHideQaAuthors,
      toggleHideAuthorsLoading,
      onToggleHideQaAuthors,

      hasNda,
      toggleNdaLoading,
      isNdaActivationDialogOpen,
      isNdaDeletionDialogOpen,
      isNdaAddedSignDialogOpen,
      prepareToggleNdaDataroomAccess,
    }
  },
})
</script>

<style lang="scss" scoped>
.AppToggle {
  :deep(.v-switch__track) {
    height: 20px;
    min-width: 36px;
    opacity: 1;
    &.bg-primary {
      opacity: 1;
    }
  }
  :deep(.v-selection-control__input) {
    width: 30px;
    height: 30px;
  }
  :deep(.v-switch__thumb) {
    height: 15px;
    width: 15px;
  }
  :deep(.v-selection-control) {
    min-height: 20px;
  }
  :deep(.v-selection-control__wrapper) {
    height: 20px;
  }
  :deep(*) {
    .v-selection-control__input {
      transform: translateX(-7px) !important;
    }
    .v-selection-control--dirty .v-selection-control__input {
      transform: translateX(7px) !important;
    }
  }
}
</style>
